import { API_BASE } from '@/shared/services/api';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Progress } from '@/shared/components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Textarea } from '@/shared/components/ui/textarea';
import { Label } from '@/shared/components/ui/label';
import {
  MapPin,
  Package,
  CheckCircle2,
  XCircle,
  Clock,
  Navigation,
  ChevronRight,
  Phone,
  GripVertical,
  AlertCircle,
  Route,
  History,
  RefreshCw,
  ArrowUpDown,
  Trash2,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';
import { useDriver } from '../context/DriverContext';
import { useDriverPermissions } from '@/shared/hooks/useMobilePermissions';

const API_URL = API_BASE;

const RouteActive = () => {
  const navigate = useNavigate();
  const { activeRoute, arriveAtStop, completeRoute, resequenceStops, geoLocation } = useDriver();
  const { canAccess, loading: permissionsLoading } = useDriverPermissions();
  const [isDragging, setIsDragging] = useState(false);
  const [showRerouteDialog, setShowRerouteDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [rerouteData, setRerouteData] = useState({ action: 'resequence', reason: 'driver_request', details: '' });
  const [routeHistory, setRouteHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Check permissions
  const canReroute = canAccess('route_reroute');
  const canViewHistory = canAccess('route_history');
  const canDeliver = canAccess('stop_deliver');

  // Fetch route history
  const fetchHistory = async () => {
    if (!activeRoute?.id) return;
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('ezerpToken');
      const res = await fetch(`${API_URL}/api/lastmile/routes/${activeRoute.id}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRouteHistory(data.history || []);
      }
    } catch (err) {
      console.error('Error fetching history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Handle reroute action
  const handleReroute = async () => {
    if (!activeRoute?.id) return;
    try {
      const token = localStorage.getItem('ezerpToken');
      const res = await fetch(`${API_URL}/api/lastmile/routes/${activeRoute.id}/reroute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(rerouteData)
      });

      if (res.ok) {
        toast.success('Route modified successfully');
        setShowRerouteDialog(false);
        // Refresh route data
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to modify route');
      }
    } catch (err) {
      toast.error('Failed to modify route');
    }
  };

  if (!activeRoute) {
    return (
      <div className="p-4 text-center py-12" data-testid="no-active-route">
        <AlertCircle className="h-12 w-12 mx-auto text-gray-400 mb-4" />
        <h2 className="text-xl font-semibold mb-2">No Active Route</h2>
        <p className="text-muted-foreground mb-4">Load a route to start deliveries</p>
        <Button onClick={() => navigate('/driver/route-load')}>
          Load Route
        </Button>
      </div>
    );
  }

  const stops = activeRoute.stops || [];
  const completedStops = stops.filter(s => s.status === 'DELIVERED' || s.status === 'FAILED');
  const progress = stops.length > 0 ? (completedStops.length / stops.length) * 100 : 0;
  const nextStop = stops.find(s => s.status === 'PENDING' || s.status === 'ARRIVED');

  const handleArriveAtStop = (stop) => {
    arriveAtStop(stop);
    navigate('/driver/stop-deliver', { state: { stopId: stop.id } });
  };

  const handleCompleteRoute = () => {
    completeRoute();
    navigate('/driver/route-complete');
    toast.success('Route Completed', { description: 'Great job!' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DELIVERED': return 'bg-green-500';
      case 'FAILED': return 'bg-red-500';
      case 'ARRIVED': return 'bg-blue-500';
      default: return 'bg-gray-300';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'DELIVERED': return <CheckCircle2 className="h-4 w-4 text-white" />;
      case 'FAILED': return <XCircle className="h-4 w-4 text-white" />;
      default: return <span className="h-2 w-2 rounded-full bg-white" />;
    }
  };

  return (
    <div className="p-4 space-y-4" data-testid="route-active">
      {/* Route Progress Header */}
      <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm opacity-80">{activeRoute.id}</p>
              <p className="text-2xl font-bold">{completedStops.length} / {stops.length}</p>
              <p className="text-sm opacity-80">stops completed</p>
            </div>
            <div className="text-right">
              <p className="text-sm opacity-80">ETA</p>
              <p className="text-xl font-bold">
                {stops.length - completedStops.length > 0 
                  ? `${(stops.length - completedStops.length) * 15} min`
                  : 'Done'
                }
              </p>
            </div>
          </div>
          <Progress value={progress} className="h-2 bg-white/20" />
        </CardContent>
      </Card>

      {/* Next Stop Highlight */}
      {nextStop && (
        <Card className="border-2 border-green-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Navigation className="h-4 w-4 text-green-500" />
              Next Stop
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-full bg-green-100 text-green-600">
                <MapPin className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold">{nextStop.customer}</h3>
                <p className="text-sm text-muted-foreground">{nextStop.address}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline">
                    <Package className="h-3 w-3 mr-1" />
                    {nextStop.packages?.length || 0} pkg
                  </Badge>
                  <Badge variant="outline">
                    <Clock className="h-3 w-3 mr-1" />
                    ~15 min
                  </Badge>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button 
                variant="outline" 
                size="sm"
                className="flex-1"
                onClick={() => window.open(`tel:+1234567890`)}
              >
                <Phone className="h-4 w-4 mr-1" />
                Call
              </Button>
              <Button 
                size="sm"
                className="flex-1 bg-green-500 hover:bg-green-600"
                onClick={() => handleArriveAtStop(nextStop)}
                data-testid="arrive-btn"
              >
                <Navigation className="h-4 w-4 mr-1" />
                I've Arrived
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stop List */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center justify-between">
            <span>All Stops</span>
            <div className="flex items-center gap-2">
              {canReroute && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowRerouteDialog(true)}
                  className="h-8 px-2"
                >
                  <ArrowUpDown className="h-4 w-4 mr-1" />
                  Reorder
                </Button>
              )}
              {canViewHistory && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => { setShowHistoryDialog(true); fetchHistory(); }}
                  className="h-8 px-2"
                >
                  <History className="h-4 w-4 mr-1" />
                  History
                </Button>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {stops.map((stop, index) => (
            <div 
              key={stop.id}
              className={`px-4 py-3 border-b last:border-0 flex items-center gap-3 ${
                stop.status === 'PENDING' && canDeliver ? 'cursor-pointer hover:bg-gray-50' : ''
              }`}
              onClick={() => stop.status === 'PENDING' && canDeliver && handleArriveAtStop(stop)}
              data-testid={`stop-${stop.id}`}
            >
              {/* Status Indicator */}
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getStatusColor(stop.status)}`}>
                {getStatusIcon(stop.status)}
              </div>
              
              {/* Stop Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Stop {index + 1}</span>
                  {stop.status !== 'PENDING' && (
                    <Badge variant={stop.status === 'DELIVERED' ? 'default' : 'destructive'} className="text-xs">
                      {stop.status}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground truncate">{stop.customer}</p>
                <p className="text-xs text-muted-foreground truncate">{stop.address}</p>
              </div>

              {/* Package Count & Arrow */}
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  {stop.packages?.length || 0} pkg
                </Badge>
                {stop.status === 'PENDING' && canDeliver && (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
                {stop.status === 'PENDING' && !canDeliver && (
                  <Lock className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Complete Route Button */}
      {completedStops.length === stops.length && stops.length > 0 && (
        <Button 
          onClick={handleCompleteRoute}
          className="w-full bg-green-500 hover:bg-green-600"
          size="lg"
          data-testid="complete-route-btn"
        >
          <CheckCircle2 className="h-5 w-5 mr-2" />
          Complete Route
        </Button>
      )}

      {/* GPS Status */}
      {geoLocation && (
        <div className="text-center text-xs text-muted-foreground">
          GPS: {(geoLocation.lat ?? 0).toFixed(4)}, {(geoLocation.lng ?? 0).toFixed(4)}
        </div>
      )}

      {/* Reroute Dialog */}
      <Dialog open={showRerouteDialog} onOpenChange={setShowRerouteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modify Route</DialogTitle>
            <DialogDescription>Change stop order or request route modification</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reason</Label>
              <Select value={rerouteData.reason} onValueChange={(v) => setRerouteData(d => ({ ...d, reason: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="traffic">Traffic</SelectItem>
                  <SelectItem value="road_closure">Road Closure</SelectItem>
                  <SelectItem value="customer_request">Customer Request</SelectItem>
                  <SelectItem value="driver_request">Driver Request</SelectItem>
                  <SelectItem value="time_window">Time Window</SelectItem>
                  <SelectItem value="emergency">Emergency</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Details (optional)</Label>
              <Textarea
                value={rerouteData.details}
                onChange={(e) => setRerouteData(d => ({ ...d, details: e.target.value }))}
                placeholder="Explain the reason for this change..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRerouteDialog(false)}>Cancel</Button>
            <Button onClick={handleReroute}>Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Route History</DialogTitle>
            <DialogDescription>Changes made to this route</DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto">
            {historyLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : routeHistory.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">No modifications recorded</p>
            ) : (
              <div className="space-y-3">
                {routeHistory.map((event, i) => (
                  <div key={i} className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <Badge variant="outline" className="text-xs">
                        {event.action?.replace(/_/g, ' ')}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {new Date(event.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm">{event.payload?.message || event.event_type}</p>
                    {event.reason && (
                      <p className="text-xs text-muted-foreground mt-1">Reason: {event.reason}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowHistoryDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RouteActive;
